import {
  Badge, Button, Card, Checkbox, Chip, Collapse, Group, Loader, Progress, Select, Stack, Text, ThemeIcon, Title,
  UnstyledButton,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconBriefcase, IconChevronDown, IconDownload, IconFileText, IconSparkles, IconTemplate, IconTrash,
} from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteGeneratedDocument, generateDocument, generateRhpDraft, getCandidates, getDocumentJobStatus,
  getGeneratedDocuments, getProjectCandidates, getProjects, getTemplates,
} from "../api";
import { downloadFile } from "../../../Boilerplate/api";
import { alphaDisplayName, sortIdsByCandidateAlpha, sortProjectCandidatesAlpha } from "../_Shared/candidateAlphaSort";
import { formatDateTime } from "../_Shared/time";
import { partitionTemplates } from "../_Shared/documentTemplates";
import { templateDescription, templateName, templatePurposeLabel } from "../_Shared/templateCopy";

const STAGE_ORDER = ["identified", "longlist", "shortlist", "interview", "finalist", "placed", "rejected"];

function pageItems(result) {
  if (Array.isArray(result)) return result;
  return result?.data ?? result?.items ?? [];
}

export function TemplateCard({ template, selected, onSelect, t }) {
  const unavailable = !template.hasFile;
  const isTypst = template.format === "typst";
  const desc = templateDescription(template, t);
  return (
    <Card
      p="sm"
      withBorder
      style={{
        cursor: unavailable ? "default" : "pointer",
        borderColor: selected ? "var(--mantine-primary-color-filled)" : undefined,
        borderWidth: selected ? 2 : 1,
        opacity: unavailable ? 0.5 : 1,
      }}
      onClick={unavailable ? undefined : onSelect}
    >
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon variant="light" size="md" color={unavailable ? "gray" : undefined}>
          <IconTemplate size={16} />
        </ThemeIcon>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs">
            <Text size="sm" fw={500}>{templateName(template, t)}</Text>
            {unavailable && <Badge size="xs" variant="light" color="gray">{t("no_file_uploaded")}</Badge>}
            {template.isDefault && <Badge size="xs" variant="light" color="blue">{t("default_template")}</Badge>}
            <Badge size="xs" variant="outline" color={isTypst ? "violet" : "gray"}>
              {isTypst ? "PDF" : "DOCX + PDF"}
            </Badge>
          </Group>
          {selected && desc && (
            <Text size="xs" c="dimmed" mt={4}>{desc}</Text>
          )}
        </div>
      </Group>
    </Card>
  );
}

export function GeneratedDocumentsList({ docs, onDelete }) {
  const { t, i18n } = useTranslation("Veft");
  if (!docs?.length) {
    return <Text size="sm" c="dimmed">{t("no_generated_documents")}</Text>;
  }
  return (
    <Stack gap="sm">
      {docs.map((doc) => (
        <Card key={doc.id} p="sm" withBorder>
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              <ThemeIcon variant="light" size="lg" color="green">
                <IconFileText size={18} />
              </ThemeIcon>
              <div style={{ minWidth: 0 }}>
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" fw={500} truncate>{templatePurposeLabel(doc.purpose, t)}</Text>
                  <Badge size="xs" variant="light">{t("n_candidates", { count: doc.candidateCount })}</Badge>
                </Group>
                <Text size="xs" c="dimmed">
                  {t("generated_at", { date: formatDateTime(doc.generatedAt, i18n.language) })}
                </Text>
              </div>
            </Group>
            <Group gap="xs" wrap="nowrap">
              {doc.hasDocx && (
                <Button
                  variant="light" size="xs"
                  leftSection={<IconDownload size={14} />}
                  onClick={() => downloadFile(`documents/${doc.id}/download/docx`)}
                >
                  .docx
                </Button>
              )}
              {doc.hasPdf && (
                <Button
                  variant="light" size="xs"
                  leftSection={<IconDownload size={14} />}
                  onClick={() => downloadFile(`documents/${doc.id}/download/pdf`)}
                >
                  .pdf
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="subtle" size="xs" color="red"
                  onClick={() => onDelete(doc.id)}
                  aria-label={t("delete")}
                >
                  <IconTrash size={14} />
                </Button>
              )}
            </Group>
          </Group>
        </Card>
      ))}
    </Stack>
  );
}

/**
 * Template picker + candidate checkboxes + generate.
 * When `lockProject` is set, the project Select is hidden (used inside a project).
 * Names are loaded for the *project's* candidates, not a global 500-row page.
 */
export default function DocumentGenerator({
  projectId: lockedProjectId,
  lockProject = false,
  projectCandidates: projectCandidatesProp,
  candidateMap: candidateMapProp,
  onGenerated,
  showGeneratedList = false,
}) {
  const { t } = useTranslation("Veft");

  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [pickedProject, setPickedProject] = useState(lockedProjectId ?? null);
  const [ownProjectCandidates, setOwnProjectCandidates] = useState([]);
  const [ownCandidateMap, setOwnCandidateMap] = useState({});
  const [generatedDocs, setGeneratedDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [jobProgress, setJobProgress] = useState(null);
  const [remarksFilledIds, setRemarksFilledIds] = useState(() => new Set());
  const [moreTemplatesOpen, setMoreTemplatesOpen] = useState(false);
  const pollRef = useRef(null);

  const selectedProject = lockProject ? (lockedProjectId ?? null) : pickedProject;
  const projectCandidates = useMemo(() => {
    if (!selectedProject && !lockProject) return [];
    return projectCandidatesProp ?? ownProjectCandidates;
  }, [selectedProject, lockProject, projectCandidatesProp, ownProjectCandidates]);
  const candidateMap = candidateMapProp ?? ownCandidateMap;
  const sortedProjectCandidates = useMemo(
    () => sortProjectCandidatesAlpha(projectCandidates, candidateMap),
    [projectCandidates, candidateMap],
  );

  const { pinned: pinnedTemplates, rest: restTemplates } = useMemo(
    () => partitionTemplates(templates),
    [templates],
  );
  const defaultTemplateId = useMemo(() => {
    const pool = [...pinnedTemplates, ...restTemplates].filter((t) => t.hasFile);
    return (pool.find((t) => t.purpose === "longlist") ?? pool[0])?.id ?? null;
  }, [pinnedTemplates, restTemplates]);
  if (selectedTemplate == null && defaultTemplateId) {
    setSelectedTemplate(defaultTemplateId);
  }
  const selectedInRest = restTemplates.some((t) => t.id === selectedTemplate);
  const moreOpen = moreTemplatesOpen || selectedInRest;
  const selectedTemplateObj = templates.find((tpl) => tpl.id === selectedTemplate);
  const isRejectedTemplate = selectedTemplateObj?.purpose === "rejectedCandidates";

  const defaultSelectedIds = useMemo(
    () => projectCandidates
      .filter((pc) => (isRejectedTemplate ? pc.stage === "rejected" : pc.stage !== "rejected"))
      .map((pc) => pc.candidateId),
    [projectCandidates, isRejectedTemplate],
  );
  const selectionKey = `${selectedProject ?? ""}:${isRejectedTemplate}:${defaultSelectedIds.join(",")}`;
  const [selection, setSelection] = useState({ key: selectionKey, ids: defaultSelectedIds });
  if (selection.key !== selectionKey) {
    setSelection({ key: selectionKey, ids: defaultSelectedIds });
  }
  const selectedCandidateIds = selection.ids;

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    const load = lockProject
      ? getTemplates().then((tpl) => { setTemplates(tpl ?? []); })
      : Promise.all([getTemplates(), getProjects()]).then(([tpl, proj]) => {
        setTemplates(tpl ?? []);
        setProjects(proj ?? []);
      });
    load.finally(() => setLoading(false));
  }, [lockProject]);

  // Load project candidates + names when we don't receive them from the parent.
  useEffect(() => {
    if (projectCandidatesProp || !selectedProject) return undefined;
    let cancelled = false;
    getProjectCandidates(selectedProject).then(async (pcs) => {
      if (cancelled) return;
      const list = pcs ?? [];
      setOwnProjectCandidates(list);
      const result = await getCandidates(1, 500, null, selectedProject);
      if (cancelled) return;
      const items = pageItems(result);
      setOwnCandidateMap(Object.fromEntries(items.map((c) => [c.id, c])));
    });
    if (showGeneratedList) {
      getGeneratedDocuments(selectedProject).then((docs) => {
        if (!cancelled) setGeneratedDocs(docs ?? []);
      });
    }
    return () => { cancelled = true; };
  }, [selectedProject, projectCandidatesProp, showGeneratedList]);

  const candidatesByStage = projectCandidates.reduce((acc, pc) => {
    (acc[pc.stage] ??= []).push(pc.candidateId);
    return acc;
  }, {});

  const setSelectedCandidateIds = useCallback((updater) => {
    setSelection((prev) => {
      const nextIds = typeof updater === "function" ? updater(prev.ids) : updater;
      return { key: prev.key, ids: nextIds };
    });
  }, []);

  const toggleCandidate = useCallback((candidateId) => {
    setSelectedCandidateIds((prev) =>
      prev.includes(candidateId)
        ? prev.filter((id) => id !== candidateId)
        : [...prev, candidateId],
    );
  }, [setSelectedCandidateIds]);

  const toggleStage = useCallback((stage) => {
    const stageIds = candidatesByStage[stage] ?? [];
    if (stageIds.length === 0) return;
    setSelectedCandidateIds((prev) => {
      const stageSet = new Set(stageIds);
      const allSelected = stageIds.every((id) => prev.includes(id));
      if (allSelected) return prev.filter((id) => !stageSet.has(id));
      const next = new Set(prev);
      stageIds.forEach((id) => next.add(id));
      return [...next];
    });
  }, [candidatesByStage, setSelectedCandidateIds]);

  async function refreshDocs(projectId) {
    if (!showGeneratedList || !projectId) return;
    const docs = await getGeneratedDocuments(projectId);
    setGeneratedDocs(docs ?? []);
  }

  function pcsMissingRemarks() {
    return projectCandidates.filter((pc) =>
      selectedCandidateIds.includes(pc.candidateId)
      && !remarksFilledIds.has(pc.id)
      && !(pc.annotations ?? []).some((a) => (a ?? "").trim()));
  }

  async function waitForDrafts(pcIds) {
    const deadline = Date.now() + 180_000;
    while (Date.now() < deadline) {
      const list = await getProjectCandidates(selectedProject);
      const pending = list.filter((pc) => pcIds.includes(pc.id) && pc.draftStatus === "generating");
      if (pending.length === 0) {
        const failed = list.filter((pc) => pcIds.includes(pc.id) && pc.draftStatus === "failed");
        return failed.length;
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    return -1;
  }

  async function createMissingRemarks(missing) {
    const queued = [];
    for (const pc of missing) {
      if (pc.draftStatus === "generating") {
        queued.push(pc.id);
        continue;
      }
      try {
        await generateRhpDraft(selectedProject, pc.id, false, "longlistCopy");
        queued.push(pc.id);
      } catch (err) {
        if (err?.status === 402) throw err;
        // 409 already-running still counts as queued
        if (pc.draftStatus === "generating" || /already/i.test(err?.message || ""))
          queued.push(pc.id);
      }
    }
    if (queued.length === 0) return 0;
    const failed = await waitForDrafts(queued);
    setRemarksFilledIds((prev) => {
      const next = new Set(prev);
      queued.forEach((id) => next.add(id));
      return next;
    });
    return failed;
  }

  function handleGenerate() {
    if (!selectedProject || !selectedTemplate || selectedCandidateIds.length === 0) return;
    const purpose = selectedTemplateObj?.purpose;
    const needsRemarks = purpose === "longlist" || purpose === "shortlist";
    const missing = needsRemarks ? pcsMissingRemarks() : [];
    if (missing.length === 0) {
      startGenerate();
      return;
    }
    modals.open({
      title: t("remarks_missing_title"),
      centered: true,
      children: (
        <Stack gap="md">
          <Text size="sm">{t("remarks_missing_body", { count: missing.length })}</Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => { modals.closeAll(); startGenerate(); }}>
              {t("remarks_missing_anyway")}
            </Button>
            <Button
              onClick={async () => {
                modals.closeAll();
                setGenerating(true);
                notifications.show({
                  title: t("remarks_creating"),
                  message: t("remarks_missing_body", { count: missing.length }),
                  color: "blue",
                });
                try {
                  const failed = await createMissingRemarks(missing);
                  if (failed > 0 || failed === -1) {
                    notifications.show({
                      title: t("draft_failed"),
                      message: t("remarks_create_failed"),
                      color: "yellow",
                    });
                  }
                } catch (err) {
                  setGenerating(false);
                  notifications.show({
                    title: t("draft_failed"),
                    message: err?.message || t("remarks_create_failed"),
                    color: "red",
                  });
                  return;
                }
                startGenerate();
              }}
            >
              {t("remarks_missing_create")}
            </Button>
          </Group>
        </Stack>
      ),
    });
  }

  async function startGenerate() {
    if (!selectedProject || !selectedTemplate || selectedCandidateIds.length === 0) return;
    // Surname order, same comparator as the pipeline board — not checkbox-toggle
    // order, and not GetByProjectAsync insertion order.
    const orderedIds = sortIdsByCandidateAlpha(selectedCandidateIds, candidateMap);
    setGenerating(true);
    setJobProgress(null);
    try {
      const result = await generateDocument({
        projectId: selectedProject,
        templateId: selectedTemplate,
        candidateIds: orderedIds,
      });
      notifications.show({
        title: t("generation_started"),
        message: t("generation_started_message", { count: orderedIds.length }),
        color: "blue",
      });
      const jobId = result.jobId;
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const job = await getDocumentJobStatus(jobId);
          setJobProgress(job);
          if (job.status === "success") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setGenerating(false);
            notifications.show({ title: t("generation_done"), message: t("generation_done_message"), color: "green" });
            await refreshDocs(selectedProject);
            onGenerated?.();
          } else if (job.status === "failed") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setGenerating(false);
            notifications.show({ title: t("generation_failed"), message: t("generation_failed_message"), color: "red" });
          }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (err) {
      setGenerating(false);
      notifications.show({
        title: t("error"),
        message: err.message || t("generation_failed_message"),
        color: "red",
      });
    }
  }

  async function handleDelete(docId) {
    try {
      await deleteGeneratedDocument(docId);
      setGeneratedDocs((prev) => prev.filter((d) => d.id !== docId));
      onGenerated?.();
      notifications.show({ title: t("deleted_notification"), message: t("document_removed"), color: "green" });
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("delete_document_failed") });
    }
  }

  const projectOptions = projects.map((p) => ({ value: p.id, label: p.title }));
  const template = templates.find((tpl) => tpl.id === selectedTemplate);

  if (loading) return <Loader />;

  return (
    <Stack gap="lg">
      <Card>
        <Title order={5} mb="sm">{t("choose_template")}</Title>
        <Stack gap="xs">
          {pinnedTemplates.map((tpl) => (
            <TemplateCard
              key={tpl.id}
              template={tpl}
              selected={selectedTemplate === tpl.id}
              onSelect={() => setSelectedTemplate(tpl.id)}
              t={t}
            />
          ))}
          {restTemplates.length > 0 && (
            <>
              <UnstyledButton
                onClick={() => { if (!selectedInRest) setMoreTemplatesOpen((o) => !o); }}
                style={{ alignSelf: "flex-start", cursor: selectedInRest ? "default" : "pointer" }}
              >
                <Group gap={4}>
                  <IconChevronDown
                    size={14}
                    style={{
                      transform: moreOpen ? "rotate(180deg)" : undefined,
                      transition: "transform 150ms ease",
                    }}
                  />
                  <Text size="sm" c="dimmed">
                    {t("more_templates", { count: restTemplates.length })}
                  </Text>
                </Group>
              </UnstyledButton>
              <Collapse in={moreOpen}>
                <Stack gap="xs">
                  {restTemplates.map((tpl) => (
                    <TemplateCard
                      key={tpl.id}
                      template={tpl}
                      selected={selectedTemplate === tpl.id}
                      onSelect={() => setSelectedTemplate(tpl.id)}
                      t={t}
                    />
                  ))}
                </Stack>
              </Collapse>
            </>
          )}
          {templates.length === 0 && (
            <Text size="sm" c="dimmed">{t("no_templates")}</Text>
          )}
        </Stack>
      </Card>

      {selectedTemplate && template?.hasFile && (
        <Card>
          <Title order={5} mb="md">{t("select_data_sources")}</Title>

          {!lockProject && (
            <Select
              label={t("project")}
              placeholder={t("select_project")}
              data={projectOptions}
              value={selectedProject}
              onChange={setPickedProject}
              leftSection={<IconBriefcase size={16} />}
              searchable
              mb="md"
            />
          )}

          {selectedProject && projectCandidates.length > 0 && (
            <>
              <Text size="sm" fw={500} mb="xs">
                {t("candidates_selected", { selected: selectedCandidateIds.length, total: projectCandidates.length })}
              </Text>
              <Group gap="xs" mb="xs">
                {STAGE_ORDER
                  .filter((stage) => candidatesByStage[stage]?.length)
                  .map((stage) => {
                    const stageIds = candidatesByStage[stage];
                    const selectedInStage = stageIds.filter((id) => selectedCandidateIds.includes(id)).length;
                    const allSelected = selectedInStage === stageIds.length;
                    return (
                      <Chip
                        key={stage}
                        size="xs"
                        checked={allSelected}
                        variant={selectedInStage === 0 ? "outline" : "filled"}
                        onChange={() => toggleStage(stage)}
                      >
                        {t(`stage_${stage}`)} ({selectedInStage}/{stageIds.length})
                      </Chip>
                    );
                  })}
              </Group>
              <Stack gap={4} mb="md">
                {sortedProjectCandidates.map((pc) => {
                  const candidate = candidateMap[pc.candidateId];
                  const label = candidate
                    ? alphaDisplayName(candidate)
                    : t("unknown_candidate");
                  return (
                    <Checkbox
                      key={pc.candidateId}
                      label={
                        <Group gap="xs">
                          <Text size="sm">{label}</Text>
                          {pc.ranking && <Badge size="xs" variant="light">{pc.ranking}</Badge>}
                          <Badge size="xs" variant="outline" style={{ textTransform: "none" }}>
                            {t(`stage_${pc.stage}`)}
                          </Badge>
                        </Group>
                      }
                      checked={selectedCandidateIds.includes(pc.candidateId)}
                      onChange={() => toggleCandidate(pc.candidateId)}
                    />
                  );
                })}
              </Stack>
            </>
          )}

          {selectedProject && projectCandidates.length === 0 && (
            <Text size="sm" c="dimmed" mb="md">{t("no_candidates_on_project")}</Text>
          )}

          {generating && jobProgress && (
            <div style={{ marginBottom: 16 }}>
              <Text size="xs" c="dimmed" mb={4}>
                {jobProgress.progress < 0.8 ? t("enriching_candidates") : t("rendering_document")}
              </Text>
              <Progress value={(jobProgress.progress ?? 0) * 100} animated />
            </div>
          )}

          <Group>
            <Button
              leftSection={<IconSparkles size={16} />}
              onClick={handleGenerate}
              loading={generating}
              disabled={!selectedProject || selectedCandidateIds.length === 0}
            >
              {t("generate_document", { count: selectedCandidateIds.length })}
            </Button>
          </Group>
        </Card>
      )}

      {showGeneratedList && selectedProject && (
        <Card>
          <Title order={5} mb="md">{t("generated_documents")}</Title>
          <GeneratedDocumentsList docs={generatedDocs} onDelete={handleDelete} />
        </Card>
      )}
    </Stack>
  );
}
